import { useState, useEffect, useMemo, useCallback } from 'react';

import { useQueryParams } from 'helpers/hooks';
import { regionDataToLocations } from 'helpers/locations';
import { useOpportunityTypes, useOpportunitiesActiveTab } from 'helpers/opportunities';
import {
  SORT_BY,
  PROJECT_STATUS,
  PROJECT_SOURCE,
  PROJECT_VIEW_STATUS,
} from 'constants/opportunities';
import { allLocations } from 'constants/regions';

export const baseFilter = {
  sortBy: SORT_BY.mostRecent,
  locations: allLocations,
  statuses: PROJECT_STATUS.open,
  sources: PROJECT_SOURCE.allSources,
  viewStatus: PROJECT_VIEW_STATUS.allStatuses,
  types: [],
  organizations: [],
  limit: 10,
};

const searchProfileToFilter = profile => ({
  id: profile.id,
  sortBy: profile.status === PROJECT_STATUS.closed ? SORT_BY.closingSoon : SORT_BY.mostRecent,
  organizations: profile.organizations,
  locations: regionDataToLocations(profile.regionData),
  types: profile.projectTypes,
  // null = all
  viewStatus: profile.viewedProjects || PROJECT_VIEW_STATUS.allStatuses,
  statuses: profile.status || PROJECT_STATUS.allOpportunities,
  sources: profile.source || PROJECT_SOURCE.allSources,
});

export const useOpportunitiesSearchFilter = ({ searchProfile }) => {
  const query = useQueryParams();
  const { tab } = useOpportunitiesActiveTab();

  const opportunityTypesQuery = useOpportunityTypes({
    onSuccess: types => {
      setCurrentSearchFilter(prev => ({ ...prev, types }));
    },
  });

  const [currentSearchFilter, setCurrentSearchFilter] = useState({
    ...baseFilter,
    types: opportunityTypesQuery.data || [],
    search: query.get('search') || '',
  });
  const [currentSearchProfileFilter, setCurrentSearchProfileFilter] = useState({
    ...baseFilter,
    search: query.get('search') || '',
    id: null,
  });

  useEffect(() => {
    if (searchProfile) {
      setCurrentSearchProfileFilter(curr => ({
        ...curr,
        ...searchProfileToFilter(searchProfile),
      }));
    }
  }, [searchProfile]);

  const effectiveFilter = useMemo(() => {
    return tab === 'saved' ? currentSearchProfileFilter : currentSearchFilter;
  }, [tab, currentSearchFilter, currentSearchProfileFilter]);

  const setEffectiveFilter = useMemo(() => {
    return tab === 'saved' ? setCurrentSearchProfileFilter : setCurrentSearchFilter;
  }, [tab]);

  const getInitialEffectiveFilter = useCallback(
    availableLocations => {
      return tab === 'saved' && searchProfile
        ? searchProfileToFilter(searchProfile)
        : { ...baseFilter, types: opportunityTypesQuery.data || [], locations: availableLocations };
    },
    [tab, opportunityTypesQuery.data, searchProfile]
  );

  return {
    currentSearchFilter,
    currentSearchProfileFilter,
    effectiveFilter,
    setCurrentSearchProfileFilter,
    setEffectiveFilter,
    getInitialEffectiveFilter,
  };
};
